import React, {useState} from "react"
import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import {createBillpayOrgSettings} from "../../resources/billpayOrgSettings"
import {v4 as uuidv4} from "uuid"
import {findOrgs, Org} from "../../resources/org"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import Loading from "../../components/Loading/Loading"

interface Props {
	close: () => void
	onSuccess: () => void
}

function ModalCheckboxField({
	label,
	checked,
	onChange,
}: {
	label: string
	checked: boolean
	onChange: (v: boolean) => void
}) {
	return (
		<div className="field">
			<label className="label">{label}</label>
			<div className="control">
				<label className="checkbox">
					<input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />{" "}
					{checked ? "Enabled" : "Disabled"}
				</label>
			</div>
		</div>
	)
}

function CreateForm({orgs, close, onSuccess}: {orgs: Org[]} & Props) {
	const accessToken = useAccessToken()
	const [orgId, setOrgId] = useState(orgs[0]?.id ?? "")
	const [enableCustomerLevelCategories, setEnableCustomerLevelCategories] = useState(false)
	const [bankingTermsId, setBankingTermsId] = useState("")
	const [autoLinkAccounts, setAutoLinkAccounts] = useState(false)

	const [requestState, executeRequest] = useAsyncResultIdle(
		() =>
			createBillpayOrgSettings(
				accessToken,
				orgId,
				enableCustomerLevelCategories,
				bankingTermsId,
				autoLinkAccounts,
				uuidv4()
			),
		[accessToken, orgId, enableCustomerLevelCategories, bankingTermsId, autoLinkAccounts]
	)

	return (
		<AsyncResultModal
			title="Create Bill Pay Org Settings"
			buttonText="Create"
			buttonClassname="is-success"
			cancelButtonText="Cancel"
			cancelButtonClassname="is-white"
			state={requestState}
			onSubmit={executeRequest}
			onSuccess={onSuccess}
			successText="Bill Pay Org Settings created successfully!"
			errorToText={(err: any) => err.errors?.[0]?.title || "An error occurred"}
			close={close}
			buttonDisabled={!orgId || !bankingTermsId}
		>
			<div className="field">
				<label className="label">Org *</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
							<option value="">Select an org...</option>
							{orgs.map((org) => (
								<option key={org.id} value={org.id}>
									{org.attributes.name} (ID: {org.id})
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			<div className="field">
				<label className="label">Banking Terms ID *</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Enter banking terms ID"
						value={bankingTermsId}
						onChange={(e) => setBankingTermsId(e.target.value)}
						required
					/>
				</div>
			</div>

			<ModalCheckboxField
				label="Enable Customer Level Categories"
				checked={enableCustomerLevelCategories}
				onChange={setEnableCustomerLevelCategories}
			/>
			<ModalCheckboxField label="Auto Link Accounts" checked={autoLinkAccounts} onChange={setAutoLinkAccounts} />
		</AsyncResultModal>
	)
}

export default function CreateBillPayOrgSettingsModal({close, onSuccess}: Props) {
	const accessToken = useAccessToken()
	const orgsResult = useAsyncResult(() => findOrgs(accessToken, 0, 10000))

	return orgsResult.match(
		() => (
			<div className="modal is-active">
				<div className="modal-background" />
				<div className="modal-card">
					<Loading />
				</div>
			</div>
		),
		(orgs) => <CreateForm orgs={orgs} close={close} onSuccess={onSuccess} />,
		() => (
			<div className="modal is-active">
				<div className="modal-background" onClick={close} />
				<div className="modal-card">
					<header className="modal-card-head">
						<p className="modal-card-title">Error</p>
						<button className="delete" aria-label="close" onClick={close} />
					</header>
					<section className="modal-card-body">Failed to load orgs.</section>
				</div>
			</div>
		)
	)
}
